/**
 * Pagination — 게시판 페이지 네비게이션
 *
 * 현재 페이지, 총 페이지 수를 기반으로 페이지 버튼 렌더.
 * 5페이지 미만이면 전체 표시, 이상이면 앞뒤 생략(...)으로 축약.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import { cn } from '../../lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;      // 0-based
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage, totalItems, pageSize = 20, onPageChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // 1페이지 이하면 렌더링 안 함
  if (totalPages <= 1) return null;

  // 표시할 페이지 번호 계산
  const getPageNumbers = (): (number | 'dots')[] => {
    const pages: (number | 'dots')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // 전체 표시
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      // 앞부분
      pages.push(0);

      if (currentPage > 2) pages.push('dots');

      // 현재 페이지 주변
      const start = Math.max(1, currentPage - 1);
      const end = Math.min(totalPages - 2, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 3) pages.push('dots');

      // 마지막
      pages.push(totalPages - 1);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-1 py-3">
      {/* 이전 */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
        className={cn(
          'size-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer',
          currentPage === 0
            ? 'text-muted-foreground/30 cursor-not-allowed'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <ChevronLeft className="size-4" />
      </button>

      {/* 페이지 번호 */}
      {getPageNumbers().map((page, idx) => {
        if (page === 'dots') {
          return (
            <span key={`dots-${idx}`} className="size-8 flex items-center justify-center text-xs text-muted-foreground">
              ···
            </span>
          );
        }

        const isActive = page === currentPage;
        return (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={cn(
              'size-8 rounded-lg text-label font-medium transition-all duration-150 cursor-pointer',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {page + 1}
          </button>
        );
      })}

      {/* 다음 */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages - 1}
        className={cn(
          'size-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer',
          currentPage >= totalPages - 1
            ? 'text-muted-foreground/30 cursor-not-allowed'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
};

export default Pagination;
